import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Home, Calendar, Clock, Users, ArrowLeft, User, CheckCircle, RefreshCw, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PER_PAGE = 10;

const BrokerEvents = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assigned');
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (agent?.role === 'broker') fetchEvents();
  }, [agent]);

  // Reset page when tab or search changes
  useEffect(() => { setPage(1); }, [activeTab, searchQuery]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/broker/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEvents();
    setRefreshing(false);
    toast.success('List refreshed');
  };

  const assignedEvents = events.filter(e => e.status !== 'done');
  const completedEvents = events.filter(e => e.status === 'done');

  const currentList = activeTab === 'assigned' ? assignedEvents : completedEvents;

  // Filter by search
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return currentList;
    const q = searchQuery.toLowerCase();
    return currentList.filter(e =>
      (e.property_address || '').toLowerCase().includes(q) ||
      (e.agent_name || '').toLowerCase().includes(q)
    );
  }, [currentList, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const formatDate = (date) => {
    if (!date) return '';
    const [y, m, d] = date.split('-');
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderEventRow = (event, isDone) => (
    <Card
      key={event.id}
      className={`bg-white px-5 py-4 rounded-xl card-shadow ${isDone ? 'border-l-4 border-l-stone-300' : ''}`}
      data-testid={`broker-event-${event.id}`}
    >
      <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
        <div className={`p-2 rounded-full flex-shrink-0 hidden sm:flex ${isDone ? 'bg-stone-100' : 'bg-[#1A4D2E]/10'}`}>
          <Home className={isDone ? 'text-stone-500' : 'text-[#1A4D2E]'} size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[#1A4D2E] truncate text-base" style={{fontFamily: '"Playfair Display", serif'}}>{event.property_address}</h3>
          <div className="flex items-center gap-3 text-xs text-stone-500 mt-1 flex-wrap">
            {event.agent_name && <span className="flex items-center gap-1"><User size={13} />{event.agent_name}</span>}
            <span className="flex items-center gap-1"><Calendar size={13} />{formatDate(event.date)}</span>
            <span className="flex items-center gap-1"><Clock size={13} />{event.time}</span>
            <span className="flex items-center gap-1"><Users size={13} />{event.visitor_count} visitors</span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${isDone ? 'bg-stone-100 text-stone-600' : 'bg-green-100 text-green-700'}`}>
          {isDone ? 'Done' : event.status || 'Active'}
        </span>
      </div>
    </Card>
  );

  if (agent?.role !== 'broker') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-lg text-stone-600">This page is only accessible to brokers</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/broker')}
            className="mb-4 text-stone-600 hover:text-[#1A4D2E]"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Dashboard
          </Button>
        </div>
        
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Assigned Open Houses
          </h1>
          <p className="text-stone-600">View all open house events created from your assignments</p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search by address or agent name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="broker-events-search"
          />
        </div>

        {/* Tabs + Refresh */}
        <div className="flex gap-2 mb-6" data-testid="broker-events-tabs">
          <button
            onClick={() => setActiveTab('assigned')}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeTab === 'assigned' ? 'bg-[#1A4D2E] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            data-testid="tab-assigned-oh"
          >
            Assigned OH ({assignedEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${activeTab === 'completed' ? 'bg-[#1A4D2E] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            data-testid="tab-completed-oh"
          >
            Completed OH ({completedEvents.length})
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2.5 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all ml-auto"
            data-testid="refresh-events-btn"
            title="Refresh list"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Results info */}
        {!loading && filtered.length > 0 && (
          <p className="text-xs text-stone-500 mb-3" data-testid="results-info">
            Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length} events
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        )}

        {loading ? (
          <p className="text-stone-500">Loading events...</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
            {searchQuery ? (
              <>
                <Search className="mx-auto mb-4 text-stone-300" size={48} />
                <p className="text-lg text-stone-600">No events match "{searchQuery}"</p>
                <p className="text-sm text-stone-500 mt-2">Try a different search term</p>
              </>
            ) : activeTab === 'assigned' ? (
              <>
                <Home className="mx-auto mb-4 text-stone-300" size={48} />
                <p className="text-lg text-stone-600">No assigned open houses</p>
                <p className="text-sm text-stone-500 mt-2">Your agents haven't created any active open house events</p>
              </>
            ) : (
              <>
                <CheckCircle className="mx-auto mb-4 text-stone-300" size={48} />
                <p className="text-lg text-stone-600">No completed open houses yet</p>
                <p className="text-sm text-stone-500 mt-2">Open houses marked as done will appear here</p>
              </>
            )}
          </Card>
        ) : (
          <>
            <div className="space-y-3" data-testid={`broker-${activeTab}-events-list`}>
              {paginated.map((event) => renderEventRow(event, activeTab === 'completed'))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="broker-events-pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="rounded-full px-3"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-full"
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-stone-600 px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-full"
                >
                  <ChevronRight size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="rounded-full px-3"
                >
                  Last
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default BrokerEvents;
